// MACHINERY V2 (Stroje) — machine profile shell, layout C (M2.4).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.4 + data package §1
// (Profile Shell) / §2 (Identity). Artboards 0504 (layout C wireframe), 0505
// (rationale), 0600 (integration model). Route: `/machinery-v2/:id`.
//
// STRUCTURE (manager ruling 2026-08-22 — the designer layout wins):
//     breadcrumb -> [decommission banner] -> HERO -> pill tabs -> tab body
// There is NO left identity column: layout C folded the sidebar into the hero
// ("just rotated", 0505) and §2 field parity is satisfied inside it. There is
// also NO KPI strip — §9 D3 cut the artboard's four tiles, so the hero flows
// directly into the tabs.
//
// ONE TELEMETRY MECHANISM. The hero's live ribbon reads the SAME
// `useFleetTelemetry` hook the fleet uses, called once here with this machine's
// id. A second poller (or a bespoke single-machine endpoint) would be a second
// truth about the same gateway; there is exactly one, and it stays visibility-
// paused and fail-closed for free.
//
// ONE STATE DERIVATION. `deriveCardPresentation` runs once, here, and its
// output feeds BOTH the hero ribbon and the Live-tab gate (profileTabContract.ts).
// Nothing downstream re-tests raw telemetry.
//
// READINESS CARD PLACEMENT — CALLED OUT DELIBERATELY. The layout-C hi-fi
// (machinery-profile-mash.jsx) is SILENT on readiness: it contains no readiness
// reference at all, and artboard 0600 does not place one either. The designer's
// only placement (machinery-readiness.jsx `RdProfileFragment`: "identity card ->
// readiness card -> actions") belongs to the LEFT COLUMN that layout C deletes.
// Per the manager's ruling for exactly this case, the card is therefore mounted
// at the TOP OF THE TAB BODY AREA, above the active tab's content.
//
// Its render gate mirrors the LIVE PROFILE, not the fleet: v1's
// MidasReadinessProfileCard returns null only when `catalogModel` is absent, so
// a decommissioned catalog machine still shows it. (The fleet additionally hides
// it for decommissioned machines — that is a card rule, not a profile rule.)
//
// THE FETCH IS TRACKED PER ID, COMPONENT-LOCALLY (M2.4 review). Two defects
// lived in reading `customerMachinery` alone, and both are fixed HERE and not
// in the slice — the LIVE v1 profile shares that slice and stays byte-
// untouched:
//   1. "Machine not found" painted BEFORE the fetch was dispatched. There was
//      no "fetch attempted" state: on the first render of an in-place `:id`
//      change (browser back/forward between two profiles) `machine` is still
//      the PREVIOUS machine and the slice-wide `loading` is still false,
//      because the getMachine dispatch happens in a passive effect AFTER
//      commit. The not-found branch now gates on `fetched.id === id` — this
//      id has actually been fetched — and never on `loading`.
//   2. A STALE response could strand the page permanently. Navigating A -> B
//      fast enough that A resolves AFTER B leaves the shared slot holding A;
//      the `machine.id === id` guard correctly rejects it, but nothing ever
//      re-fetches, so B read "not found" until a reload. A component-local
//      request token (`requestRef`) ignores stale fulfilments, and this
//      component keeps its OWN fulfilment as the fallback machine — so B still
//      renders B even when the shared slot was clobbered by A.
// The slice still WINS whenever it holds this id, which is what keeps the
// post-mutation `refresh()` in useProfileActions repainting the hero.
//
// A DECOMMISSIONED MACHINE HAS NO LIVE SURFACE (M2.4 review ruling D). No
// telemetry fetch, no live ribbon, no Live tab — decommission banner plus
// static identity only. Before this, `decommissioned` merely suppressed the
// no-telemetry note, so a retired machine that is still connected (the SBL-500
// qualifies) painted a green RUNNING badge and a pulsing connectivity dot. The
// gate mirrors the FLEET precedent (useFleetPageData passes NO ids in the
// decommissioned scope) so the two surfaces cannot disagree about a retired
// machine.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine } from "../../models/models";
import { getMachine } from "../../customerMachinerySlice";
import { Notice } from "../fleet/FleetNotices";
import { deriveCardPresentation, unconnectedTelemetry } from "../fleet/cardStates";
import { isGatewayLive, useFleetTelemetry } from "../fleet/fleetTelemetry";
import {
  deriveReadinessPresentation,
  readinessSummaryStrings,
} from "../readiness/readinessPresentation";
import ProfileBody from "./ProfileBody";
import { MachineryV2ProfileTab, buildProfileTabs, resolveProfileTab } from "./profileTabContract";

const FLEET_PATH = "/machinery-v2";

const MachineProfileV2: React.FC = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");

  const machine = useAppSelector((s) => s.customerMachinery.machine);

  const [chosenTab, setChosenTab] = useState<MachineryV2ProfileTab | null>(null);
  const [readinessOpen, setReadinessOpen] = useState(false);
  /** This component's own fetch outcome for THIS id: the "fetch attempted"
   *  state the shared slice does not carry, plus the machine it returned. */
  const [fetched, setFetched] = useState<{ id: string; machine: Machine | null } | null>(
    null
  );
  /** Monotonic request token — only the NEWEST fulfilment may write state. */
  const requestRef = useRef(0);

  useEffect(() => {
    if (!id) {
      navigate(FLEET_PATH);
      return;
    }
    const token = ++requestRef.current;
    setFetched(null);
    dispatch(getMachine(id))
      .unwrap()
      .then((m) => {
        if (requestRef.current === token) setFetched({ id, machine: m || null });
      })
      .catch(() => {
        // Rejected (404 / network / 403). The slice keeps whatever it held, so
        // "attempted, and produced nothing for THIS id" is recorded here.
        if (requestRef.current === token) setFetched({ id, machine: null });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Navigating profile -> profile must not keep the previous machine's tab.
  useEffect(() => {
    setChosenTab(null);
    setReadinessOpen(false);
  }, [id]);

  /**
   * `customerMachinery.machine` is SHARED state: the fleet writes it via
   * selectMachine (QR / add dialogs) and a previous profile visit leaves its own
   * machine there. Rendering it before this route's fetch resolves would paint
   * the WRONG machine's identity under this URL, so the id must match.
   */
  const current =
    machine && machine.id === id
      ? machine
      : fetched && fetched.id === id && fetched.machine
      ? fetched.machine
      : undefined;

  /** TRUE only once THIS id's own fetch settled without yielding a machine. */
  const notFound = !current && !!fetched && fetched.id === id;

  const decommissioned = current?.isDecommissioned === true;

  // ONE poller, the fleet's, scoped to this machine (the request itself is
  // parameterless — see fleetTelemetry.ts). An EMPTY id list skips the request
  // entirely, which is how a DECOMMISSIONED machine (and the pre-load window)
  // fetches no telemetry at all — the useFleetPageData precedent, verbatim.
  const machineIds = useMemo(
    () => (id && current && !decommissioned ? [id] : []),
    [id, current, decommissioned]
  );
  const telemetryMap = useFleetTelemetry(machineIds);
  const telemetry = useMemo(
    () => (id && telemetryMap.get(id)) || unconnectedTelemetry(),
    [id, telemetryMap]
  );
  const presentation = useMemo(() => deriveCardPresentation(telemetry), [telemetry]);

  const summaryStrings = useMemo(() => readinessSummaryStrings(translate), [translate]);
  const readiness = useMemo(() => {
    const catalogModel = current?.catalogModel;
    if (!catalogModel) return null;
    return deriveReadinessPresentation(
      {
        readiness: catalogModel.readiness,
        isOemPartner: catalogModel.isOemPartner,
        oemPartnerName: catalogModel.oemPartnerName,
      },
      { connected: isGatewayLive(telemetry) },
      summaryStrings
    );
  }, [current, telemetry, summaryStrings]);

  const tabs = useMemo(
    () =>
      buildProfileTabs({
        state: presentation.state,
        hasCatalogModel: !!current?.catalogModelId,
        decommissioned,
      }),
    [presentation.state, current, decommissioned]
  );
  const activeTab = resolveProfileTab(tabs, chosenTab);

  if (!current) {
    return (
      <main style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 24px" }}>
        {notFound ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <Notice text={translate("myMachinery:machineNotFound")} />
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => navigate(FLEET_PATH)}
            >
              {translate("myMachinery:backToEquipment")}
            </button>
          </div>
        ) : (
          <Notice text={translate("myMachinery:v2.profile.loading")} dashed={false} />
        )}
      </main>
    );
  }

  return (
    <ProfileBody
      machine={current}
      telemetry={telemetry}
      presentation={presentation}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setChosenTab}
      readiness={readiness}
      readinessOpen={readinessOpen}
      onReadinessOpenChange={setReadinessOpen}
      onNavigateFleet={() => navigate(FLEET_PATH)}
    />
  );
};

export default MachineProfileV2;
